export const M10_UNIT_10 = {
  id: "m10-u10-reporting-verbs",
  title: "Unit 10: Suggestions & Explanations",
  topic: "Reporting Verbs + That Clauses + Backshift",
  iconType: "file-text",
  grammarImage: "/m10-u10-reporting-verbs.jpg",
  grammarText: "Unit focus: Seven key reporting verbs (explained, admitted, added, agreed, claimed, argued, suggested) — each with its own meaning, tone, and grammatical behaviour. Every question tests the verb AND the full reported structure simultaneously.",

  sets: [

    // ===================================================================
    // SET 1: EXPLAINED (B1+)
    // ===================================================================
    {
      id: "m10-u10-set1",
      title: "Set 1: Explaining Things Clearly",
      context: `*Zagreb, Monday — Dr. Lena Kovač speaks to journalists*

Dr. Lena Kovač is 38 and has spent six years researching a new treatment
for a rare neurological condition. Today she is speaking to the press for the first time.
She had been warned the questions would be difficult.
She decided to explain everything as clearly as possible.

*Zagreb, lunes — Dr. Lena Kovač habla con periodistas*

La Dra. Lena Kovač tiene 38 años y ha pasado seis años investigando un nuevo tratamiento
para una enfermedad neurológica poco frecuente. Hoy habla con la prensa por primera vez.
Le habían advertido que las preguntas serían difíciles.
Decidió explicar todo con la mayor claridad posible.`,

      questions: [
        {
          type: "match",
          q: "Match 'explained' to its meaning and structure:",
          pairs: [
            { left: "explained", right: "explicó (neutral, informativo, sin juicio)" },
            { left: "explained + that clause", right: "'She explained that the process took years.'" },
            { left: "backshift rule", right: "Present Simple → Past Simple inside the clause" }
          ],
          a: "completed",
          successTip: "'Explained' = neutral reporting. No reluctance, no opinion — just information. 'She explained that X.' The 'that' is optional but common in formal writing! 📋"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'The treatment takes three years to develop.' → She ___ that the treatment ___ three years to develop.",
          options: ["explained / took", "explained / takes", "told / took"],
          a: "explained / took",
          tip: "Explained + that clause. Present Simple (takes) → Past Simple (took) = backshift.",
          successTip: "Two things to get right! EXPLAINED (not told/said) AND took (not takes). Both must backshift. One verb wrong = unclear meaning! 🎯"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'We have been testing the compound for six years.' → She ___ that they ___ the compound for six years.",
          options: ["explained / had been testing", "explained / have been testing", "said / had been testing"],
          a: "explained / had been testing",
          tip: "Present Perfect Continuous → Past Perfect Continuous. Explained = neutral information.",
          successTip: "Present Perfect Continuous 'have been testing' → Past Perfect Continuous 'had been testing'. Backshift moves every tense one step back! ⏪"
        },

        {
          type: "mc",
          q: "Marcus wrote: 'Dr. Lena ___ that the results ___ promising but that more trials ___ needed.'",
          options: ["explained / were / were", "explained / are / are", "told / were / were"],
          a: "explained / were / were",
          tip: "All present tenses backshift to past. Explained = neutral factual reporting.",
          successTip: "Multiple backshifts in one sentence! Every present tense in the original speech → past in reported speech. Read the whole clause before choosing! 🔍"
        },

        {
          type: "mc",
          q: "Which sentence is correct?",
          options: [
            "She explained that the data was inconclusive.",
            "She explained the data was are inconclusive.",
            "She explained that the data is inconclusive."
          ],
          a: "She explained that the data was inconclusive.",
          tip: "Is → was (backshift). 'That' included. Explained = neutral.",
          successTip: "Error detection! The second has a grammar error (was are). The third forgot to backshift (is → was). Only the first is correct! 🔎"
        },

        {
          type: "input",
          q: "Dr. Lena said: 'The compound will need further testing.' → She explained that the compound ___ ___ further testing. (backshift will → would)",
          a: "would need",
          alts: ["Would need"],
          tip: "Will → would in reported speech. Explained = neutral forward-looking statement."
        },

        {
          type: "mc",
          q: "Marcus asked about the timeline. Dr. Lena ___ that development ___ longer than anyone had anticipated.",
          options: ["explained / had taken", "explained / took", "told / had taken"],
          a: "explained / had taken",
          tip: "Past Simple in original → Past Perfect in reported speech.",
          successTip: "'Took' → 'had taken'. Past Simple backshifts to Past Perfect! And EXPLAINED = neutral factual response to a question. Perfect for scientific contexts! 🔬"
        },

        {
          type: "mc",
          q: "Choose the sentence where 'explained' is used correctly:",
          options: [
            "She explained that the process was complex.",
            "She explained me that the process was complex.",
            "She explained to told the process was complex."
          ],
          a: "She explained that the process was complex.",
          tip: "Explained + that clause. No person object between explained and that!",
          successTip: "'Explained me' ❌ — explained does NOT take a person object directly! Unlike TELL. 'She explained TO me that...' ✅ or 'She explained that...' ✅"
        },

        {
          type: "order",
          q: "Reorder: 'La doctora explicó que el experimento había durado seis años y que los resultados eran prometedores.'",
          words: ["The", "doctor", "explained", "that", "the", "experiment", "had", "lasted", "six", "years", "and", "that", "the", "results", "were", "promising."],
          a: "The doctor explained that the experiment had lasted six years and that the results were promising.",
          extra: ["told", "lasted", "are"]
        },

        {
          type: "input",
          q: "Dr. Lena's press statement: 'This research changes everything.' → She explained that this research ___ everything. (backshift)",
          a: "changed",
          alts: ["Changed"],
          successTip: "Changes → changed. Simple but essential! Explained = she wasn't boasting — she was informing. Tone matters! A scientist EXPLAINS. A politician might CLAIM. You'll see why soon! 🔬📰"
        }
      ]
    },

    // ===================================================================
    // SET 2: ADMITTED (B1+)
    // ===================================================================
    {
      id: "m10-u10-set2",
      title: "Set 2: Telling the Difficult Truth",
      context: `*Zagreb, Tuesday — the second press conference*

Marcus had done his research overnight.
He had found something in Dr. Lena's earlier publications
that contradicted part of what she had said the day before.
When he asked her about it, her answer surprised everyone in the room.

*Zagreb, martes — la segunda rueda de prensa*

Marcus había investigado durante toda la noche.
Había encontrado algo en las publicaciones anteriores de la Dra. Lena
que contradecía parte de lo que había dicho el día anterior.
Cuando le preguntó al respecto, su respuesta sorprendió a todos en la sala.`,

      questions: [
        {
          type: "match",
          q: "Match 'admitted' to its meaning and contrast:",
          pairs: [
            { left: "admitted", right: "admitió (verdad difícil, a veces con reluctancia)" },
            { left: "admitted vs explained", right: "Admitted = confesión. Explained = información neutral." },
            { left: "admitted + that clause", right: "'She admitted that the first trial had failed.'" }
          ],
          a: "completed",
          successTip: "ADMITTED carries emotional weight — it means the person knew this was difficult to say. 'She admitted that she had been wrong.' = She said it, even though it was hard! 💙"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'The first trial failed.' → She ___ that the first trial ___.",
          options: ["admitted / had failed", "explained / failed", "admitted / failed"],
          a: "admitted / had failed",
          tip: "Admitted = reluctant truth. Past Simple (failed) → Past Perfect (had failed).",
          successTip: "ADMITTED + had failed = double signal. The verb tells us she was reluctant; the backshift tells us when it happened. Both layers matter! 🎭"
        },

        {
          type: "mc",
          q: "Choose the correct verb: 'She ___ that she had overlooked one variable in the original data — something Marcus had already spotted.'",
          options: ["admitted", "explained", "added"],
          a: "admitted",
          tip: "Overlooking a variable = error. Confessing an error = admitted.",
          successTip: "Context reveals the verb! Overlooking data is a mistake she had to confess. ADMITTED = she said it even though it was uncomfortable. EXPLAINED would sound too neutral here! 🔍"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'I was not entirely sure about the compound's long-term effects.' → She ___ that she ___ not entirely sure about the compound's long-term effects.",
          options: ["admitted / had been", "explained / was", "agreed / had been"],
          a: "admitted / had been",
          tip: "Uncertainty about her own position = admission. Was → had been (backshift).",
          successTip: "Was → had been. Past Simple backshifts to Past Perfect when reported. And ADMITTED tells us this was hard for her to say publicly! 💡"
        },

        {
          type: "mc",
          q: "Which sentence uses 'admitted' correctly with the right backshift?",
          options: [
            "She admitted that the data had been misinterpreted.",
            "She admitted that the data has been misinterpreted.",
            "She admitted that the data is misinterpreted."
          ],
          a: "She admitted that the data had been misinterpreted.",
          tip: "Has been (Present Perfect) → had been (Past Perfect). Admitted = reluctant confession.",
          successTip: "Error detection again! Option 2 forgot to backshift (has been → had been). Option 3 forgot two backshifts. Only Option 1 correctly backsifts AND uses admitted appropriately! 🔎"
        },

        {
          type: "mc",
          q: "Marcus wrote: 'Dr. Lena ___ that she ___ under pressure to publish before the research ___ complete.'",
          options: ["admitted / had been / was", "explained / was / had been", "admitted / was / was"],
          a: "admitted / had been / was",
          tip: "Admission of pressure = admitted. Was (past) → had been (past perfect). Was → was (already past, no further shift needed).",
          successTip: "Three verbs in one reported clause! ADMITTED (reluctant), HAD BEEN (backshifted from was), WAS (past simple stays past — already as far back as it goes). ⏪"
        },

        {
          type: "input",
          q: "Dr. Lena said: 'I didn't share all the preliminary results.' → She admitted that she ___ ___ all the preliminary results. (backshift Past Simple negative)",
          a: "hadn't shared",
          alts: ["had not shared", "Hadn't shared"],
          tip: "Didn't share → hadn't shared. Past Simple negative → Past Perfect negative."
        },

        {
          type: "mc",
          q: "After admitting the error, Dr. Lena ___ that the core finding still ___ valid.",
          options: ["explained / was", "admitted / was", "added / had been"],
          a: "explained / was",
          tip: "After the admission, a neutral clarification = explained. Is → was (backshift).",
          successTip: "Sequence of verbs tells the story! First she ADMITTED (difficult truth), then she EXPLAINED (neutral clarification). The verb sequence shapes the whole narrative! 📰"
        },

        {
          type: "order",
          q: "Reorder: 'Ella admitió que había cometido un error, pero explicó que los resultados principales seguían siendo válidos.'",
          words: ["She", "admitted", "that", "she", "had", "made", "an", "error,", "but", "explained", "that", "the", "main", "results", "were", "still", "valid."],
          a: "She admitted that she had made an error, but explained that the main results were still valid.",
          extra: ["claimed", "told", "are"]
        },

        {
          type: "input",
          q: "Marcus's article: 'Dr. Lena ___ that the original timeline had been unrealistic. It was the most honest moment of the conference.' (admitir — past simple)",
          a: "admitted",
          alts: ["Admitted"],
          successTip: "ADMITTED = honest, courageous, sometimes painful. 'The most honest moment' confirms admitted was the right verb. Context and vocabulary always point to the correct reporting verb! 💙🔬"
        }
      ]
    },

    // ===================================================================
    // SET 3: ADDED (B1+)
    // ===================================================================
    {
      id: "m10-u10-set3",
      title: "Set 3: One More Thing...",
      context: `*Zagreb, Tuesday afternoon — Marcus writes his article*

Marcus had more material than he could use.
Dr. Lena had spoken for two hours.
Every time he thought she had finished, she had added something new.
"She was the kind of person," he wrote, "who always has one more thing to say."

*Zagreb, martes por la tarde — Marcus escribe su artículo*

Marcus tenía más material del que podía usar.
La Dra. Lena había hablado durante dos horas.
Cada vez que pensaba que había terminado, ella añadía algo nuevo.
"Era el tipo de persona," escribió, "que siempre tiene una cosa más que decir."`,

      questions: [
        {
          type: "match",
          q: "Match 'added' to its role in reported speech:",
          pairs: [
            { left: "added", right: "añadió (información extra tras una afirmación previa)" },
            { left: "added vs explained", right: "Added = suplementa lo dicho. Explained = introduce información." },
            { left: "structure", right: "She added that funding had also been a problem." }
          ],
          a: "completed",
          successTip: "'Added' always comes AFTER something else has been said. It's a continuation verb. 'She said X. She added that Y.' Y supplements X — never stands alone! ➕"
        },

        {
          type: "mc",
          q: "Dr. Lena explained the results, then ___ that a second trial ___ already been approved.",
          options: ["added / had", "explained / had", "admitted / has"],
          a: "added / had",
          tip: "Added = supplementary information. Has been → had been (backshift).",
          successTip: "ADDED after another verb = continuation. Has been approved → had been approved. Two things to track: verb choice AND backshift! ➕⏪"
        },

        {
          type: "mc",
          q: "She admitted the error, explained the cause, and ___ that the team ___ working on a solution for weeks.",
          options: ["added / had been", "added / was", "explained / had been"],
          a: "added / had been",
          tip: "Third piece of information after admitted + explained = added. Was working → had been working (backshift).",
          successTip: "Three-verb sequence: admitted (confession) → explained (clarification) → added (extra detail). Each verb has its role! ADDED always comes last in a sequence! 🎭"
        },

        {
          type: "mc",
          q: "Marcus wrote: 'Dr. Lena explained the timeline, admitted the delays, and ___ that outside funding ___ been cut mid-project.'",
          options: ["added / had", "agreed / had", "claimed / has"],
          a: "added / had",
          tip: "Third item in sequence = added. Has been cut → had been cut (backshift).",
          successTip: "Notice how three different verbs (explained, admitted, added) build a complete picture: facts + confession + extra detail. Real journalism uses exactly this sequence! 📰"
        },

        {
          type: "mc",
          q: "Which sentence uses 'added' correctly?",
          options: [
            "She added that the second phase would begin in spring.",
            "She added me that the second phase would begin in spring.",
            "She added that the second phase will begin in spring."
          ],
          a: "She added that the second phase would begin in spring.",
          tip: "Added = no person object (unlike TELL). Will → would (backshift).",
          successTip: "Error detection! 'Added me' ❌ — added doesn't take a person object. 'Will begin' ❌ — will should backshift to would. Only Option 1 is fully correct! 🔎"
        },

        {
          type: "input",
          q: "Dr. Lena: 'The results will be published next year.' → She explained the process, then added that the results ___ ___ published the following year. (backshift)",
          a: "would be",
          alts: ["Would be"],
          tip: "Will → would. Published = passive (no change). Added = extra information after explaining."
        },

        {
          type: "mc",
          q: "Marcus: 'Dr. Lena explained her methodology. She ___ that three other universities ___ conducting parallel research.'",
          options: ["added / were", "told / are", "admitted / had been"],
          a: "added / were",
          tip: "Supplementary information about other universities = added. Are → were (backshift).",
          successTip: "ADDED = I want to tell you one more thing. It's supplementary, not corrective (admitted) or primary (explained). Context: 'also', 'furthermore', 'additionally' — all hint at ADDED! ➕"
        },

        {
          type: "order",
          q: "Reorder: 'Ella explicó los resultados y añadió que el equipo había estado trabajando sin financiación externa durante seis meses.'",
          words: ["She", "explained", "the", "results", "and", "added", "that", "the", "team", "had", "been", "working", "without", "external", "funding", "for", "six", "months."],
          a: "She explained the results and added that the team had been working without external funding for six months.",
          extra: ["admitted", "told", "working"]
        },

        {
          type: "mc",
          q: "Yuki had attended the conference. She later ___ that Dr. Lena ___ visibly nervous, but ___ that her confidence ___ grown as the press conference went on.",
          options: ["admitted / had been / added / had", "explained / was / added / had", "added / was / explained / had"],
          a: "explained / had been / added / had",
          tip: "Neutral observation = explained. Supplementary positive note = added. Was → had been, has grown → had grown.",
          successTip: "Four decisions in one sentence! Verb 1 (explained), backshift 1 (had been), verb 2 (added), backshift 2 (had grown). Real reported speech demands all four! 🎯"
        },

        {
          type: "input",
          q: "Article ending: 'Dr. Lena explained the science clearly, admitted the setbacks honestly, and ___ that she wouldn't stop until the treatment reached patients.' (añadir — past simple)",
          a: "added",
          alts: ["Added"],
          successTip: "Three-verb sequence complete! Explained → admitted → added. Facts, confession, commitment. This is how great science journalism is written! 🔬✍️"
        }
      ]
    },

    // ===================================================================
    // SET 4: AGREED (B2-)
    // ===================================================================
    {
      id: "m10-u10-set4",
      title: "Set 4: Finding Common Ground",
      context: `*Zagreb, Wednesday — Dr. Lena meets her research partner Yuki*

Yuki had flown in from Tokyo to support Dr. Lena at the conference.
She had read Marcus's draft article and had some concerns.
The two sat together in a quiet corner and talked through everything.
By the end, they agreed on more than either had expected.

*Zagreb, miércoles — La Dra. Lena se reúne con su socia de investigación Yuki*

Yuki había volado desde Tokio para apoyar a la Dra. Lena en la conferencia.
Había leído el borrador del artículo de Marcus y tenía algunas preocupaciones.
Las dos se sentaron juntas en un rincón tranquilo y hablaron de todo.
Al final, estuvieron de acuerdo en más cosas de las que ninguna esperaba.`,

      questions: [
        {
          type: "match",
          q: "Match 'agreed' to its structures:",
          pairs: [
            { left: "agreed + that clause", right: "They agreed that more trials were needed." },
            { left: "agreed + to + infinitive", right: "She agreed to share the raw data." },
            { left: "agreed vs admitted", right: "Agreed = shared position. Admitted = difficult personal truth." }
          ],
          a: "completed",
          successTip: "AGREED has two structures! + that (shared belief) or + to + infinitive (commitment to action). 'They agreed that X' = opinion. 'They agreed to do X' = decision! 🤝"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'More clinical trials are necessary.' Yuki: 'I think so too.' → They ___ that more clinical trials ___ necessary.",
          options: ["agreed / were", "agreed / are", "admitted / were"],
          a: "agreed / were",
          tip: "Shared position = agreed. Are → were (backshift).",
          successTip: "AGREED + were (not are!) = backshift applied. Both people holding the same view. 'They agreed that X was true.' = the shared belief version! 🤝"
        },

        {
          type: "mc",
          q: "After the conversation, Dr. Lena ___ to send Yuki all the raw data before the article ___  published.",
          options: ["agreed / was", "agreed / had been", "admitted / was"],
          a: "agreed / was",
          tip: "Agreed + to + infinitive (commitment). Was published = past passive (timeline).",
          successTip: "AGREED TO + infinitive = commitment. 'She agreed to send' = she promised to act. Different from 'she agreed THAT' = shared belief. Two meanings, two structures! 🔄"
        },

        {
          type: "mc",
          q: "Yuki ___ that Marcus's article ___ fair, but ___ to review the technical sections before publication.",
          options: ["agreed / was / asked", "agreed / was / agreed", "explained / had been / agreed"],
          a: "agreed / was / asked",
          tip: "Agreed that (opinion) + asked to (request). Was = backshifted. Asked to = made a request.",
          successTip: "Agreed THAT (opinion shared) ≠ Asked TO (request made). Two different verbs, two different functions, in the same sentence! Read each clause separately! 📋"
        },

        {
          type: "mc",
          q: "Which pair of sentences is correct?",
          options: [
            "They agreed that the timeline was too short. / She agreed to extend it.",
            "They agreed that the timeline was too short. / She agreed extending it.",
            "They agreed the timeline was too short. / She agreed to extending it."
          ],
          a: "They agreed that the timeline was too short. / She agreed to extend it.",
          tip: "Agreed that (+ clause) OR agreed (without that = also ok). Agreed to + infinitive (NOT gerund).",
          successTip: "Agreed to + BASE VERB (infinitive). Never 'agreed to extending' ❌. And 'agreed (that) the timeline...' — the 'that' is optional! Both omitting and keeping 'that' are correct! ✅"
        },

        {
          type: "input",
          q: "Yuki: 'The publication date should be delayed.' Dr. Lena: 'I agree completely.' → They agreed that the publication date ___ ___ delayed. (backshift should → should)",
          a: "should be",
          alts: ["Should be"],
          tip: "Should = modal that doesn't backshift. Should be delayed = passive.",
          successTip: "SHOULD doesn't backshift! It stays as 'should' in reported speech — unlike will→would, can→could. 'She said it should be delayed' ✅ not 'would be delayed'! 📌"
        },

        {
          type: "mc",
          q: "Marcus ___ that the article ___ fair. Dr. Lena ___ and ___ to provide a written statement for inclusion.",
          options: ["claimed / was / agreed / agreed", "explained / was / agreed / agreed", "explained / had been / agreed / added"],
          a: "explained / was / agreed / agreed",
          tip: "Marcus explaining his position = explained. Dr. Lena's shared agreement = agreed (that). Her commitment = agreed (to).",
          successTip: "Four verbs in sequence! EXPLAINED (Marcus's neutral info) → AGREED THAT (shared belief) → AGREED TO (commitment). Each verb earns its place! 🎯"
        },

        {
          type: "order",
          q: "Reorder: 'Estuvieron de acuerdo en que se necesitaban más datos y acordaron compartir todos los resultados preliminares.'",
          words: ["They", "agreed", "that", "more", "data", "was", "needed", "and", "agreed", "to", "share", "all", "preliminary", "results."],
          a: "They agreed that more data was needed and agreed to share all preliminary results.",
          extra: ["claimed", "sharing", "are"]
        },

        {
          type: "mc",
          q: "Yuki ___ that she ___ proud of the research, ___ that the communication ___ sometimes been unclear, but ___ to support the publication.",
          options: ["explained / was / admitted / had / agreed", "admitted / had been / added / had / agreed", "agreed / was / added / has / explained"],
          a: "explained / was / admitted / had / agreed",
          tip: "Pride = neutral explanation. Communication issues = admission. Support = agreement to act. Was, had, no further change. Three verbs, three functions!",
          successTip: "Explained (neutral pride) + admitted (honest problem) + agreed to (commitment). Three verbs in one sentence, each doing a different job. This is advanced reported speech! 🏆"
        },

        {
          type: "input",
          q: "Final agreement: 'Dr. Lena and Yuki agreed that the research ___ ready — and agreed ___ Marcus the full dataset.' (was / to give)",
          a: "was / to give",
          alts: ["Was / to give"],
          successTip: "Agreed that + was (shared belief with backshift) + agreed to give (commitment with infinitive). Both structures in one sentence. AGREED fully mastered! 🤝✅"
        }
      ]
    },

    // ===================================================================
    // SET 5: EXPLAINED vs ADMITTED — DIRECT CONTRAST (B2-)
    // ===================================================================
    {
      id: "m10-u10-set5",
      title: "Set 5: Two Verbs, Two Tones",
      context: `*Zagreb, Thursday — the final press conference*

By Thursday, Marcus had almost finished his article.
But he wanted to clarify two moments from the conference.
Both involved Dr. Lena saying the same words — but in very different ways.
"The verb you choose," Priya had once told him, "changes the entire story."

*Zagreb, jueves — la rueda de prensa final*

Para el jueves, Marcus había casi terminado su artículo.
Pero quería aclarar dos momentos de la conferencia.
Ambos implicaban a la Dra. Lena diciendo las mismas palabras, pero de maneras muy diferentes.
"El verbo que eliges," le había dicho Priya una vez, "cambia toda la historia."`,

      questions: [
        {
          type: "match",
          q: "Match the contrast between explained and admitted:",
          pairs: [
            { left: "explained", right: "Neutral. Shares information without emotional weight." },
            { left: "admitted", right: "Reluctant. Confesses something difficult or uncomfortable." },
            { left: "same facts, different verbs", right: "Change the verb → change the reader's perception entirely." }
          ],
          a: "completed",
          successTip: "'She explained she was tired' = neutral update. 'She admitted she was tired' = she was trying to hide it. Same words, completely different tone! This is why reporting verbs matter! 🎭"
        },

        {
          type: "mc",
          q: "Dr. Lena calmly described how the process worked. Marcus wrote: 'She ___ that the process ___ longer than expected.'",
          options: ["explained / had taken", "admitted / took", "explained / took"],
          a: "explained / had taken",
          tip: "Calm description = explained. Took (Past Simple) → had taken (Past Perfect).",
          successTip: "Past Simple in the original → Past Perfect in reported speech. EXPLAINED = calm, informative. Backshift still applies regardless of the reporting verb! ⏪"
        },

        {
          type: "mc",
          q: "Dr. Lena hesitated before answering, then quietly said the process had taken longer than planned. Marcus wrote: 'She ___ that the process ___ longer than planned.'",
          options: ["admitted / had taken", "explained / had taken", "added / took"],
          a: "admitted / had taken",
          tip: "Hesitation before answering = admission of something uncomfortable. Took → had taken (backshift).",
          successTip: "Same words as before — but the hesitation changes everything! ADMITTED = she didn't want to say it. EXPLAINED = she was happy to share. Context reveals the verb! 🎭"
        },

        {
          type: "mc",
          q: "Choose the sentence that uses the correct verb for this context: 'Dr. Lena had been confident from the start. She told journalists about her methodology.'",
          options: [
            "She explained that her methodology was sound.",
            "She admitted that her methodology was sound.",
            "She agreed that her methodology was sound."
          ],
          a: "She explained that her methodology was sound.",
          tip: "Confident description of own method = neutral = explained. Not a confession, not an agreement.",
          successTip: "Confidence + describing own work = EXPLAINED. If she had doubts and was confessing, it would be ADMITTED. If someone else said it and she agreed, AGREED. Context is everything! 🎯"
        },

        {
          type: "mc",
          q: "'I know there were problems in the second phase.' — Dr. Lena said this reluctantly, after a long pause.",
          options: [
            "She admitted that there had been problems in the second phase.",
            "She explained that there had been problems in the second phase.",
            "She added that there had been problems in the second phase."
          ],
          a: "She admitted that there had been problems in the second phase.",
          tip: "Reluctance + long pause = admission. Were → had been (backshift).",
          successTip: "'Long pause' = the key contextual clue! Reluctance → ADMITTED. Also: 'Were' (Past Simple in original) → 'had been' (Past Perfect in reported). Both correct! 💡"
        },

        {
          type: "mc",
          q: "Marcus wrote two versions of the same fact. Which one makes Dr. Lena sound more credible and open?",
          options: [
            "'She explained that the delays had been caused by funding issues.'",
            "'She admitted that the delays had been caused by funding issues.'"
          ],
          a: "'She explained that the delays had been caused by funding issues.'",
          tip: "Explained = open, transparent. Admitted = suggests she was hiding something.",
          successTip: "Verb choice = editorial decision! EXPLAINED = she's being open. ADMITTED = she was trying to hide it. The same facts — but two completely different portraits of the scientist! 📰"
        },

        {
          type: "input",
          q: "Priya reviewed Marcus's article. One sentence read: 'She admitted that the research was groundbreaking.' Priya said: 'Change that verb — she wasn't confessing anything.' The correct verb is: ___",
          a: "explained",
          alts: ["Explained"],
          tip: "Groundbreaking research = positive, not a confession. Explained = the right tone.",
          successTip: "Editorial instinct! 'Groundbreaking' is POSITIVE — not something you admit, because admitting implies reluctance. Priya was right: EXPLAINED is the correct verb here! 🖊️✅"
        },

        {
          type: "mc",
          q: "Final test: 'She ___ that the formula had taken years to perfect' (proud, confident). 'She ___ that the formula had taken years to perfect' (had previously claimed it took months).",
          options: ["explained / admitted", "admitted / explained", "added / agreed"],
          a: "explained / admitted",
          tip: "Proud statement = explained. Contradiction of previous claim = admitted.",
          successTip: "Same sentence, two verbs, two completely different stories! EXPLAINED = pride in hard work. ADMITTED = confession of previous dishonesty. The reporting verb IS the story! 🎭🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Ella explicó el proceso con confianza, pero admitió que los primeros resultados habían sido decepcionantes.'",
          words: ["She", "explained", "the", "process", "confidently,", "but", "admitted", "that", "the", "early", "results", "had", "been", "disappointing."],
          a: "She explained the process confidently, but admitted that the early results had been disappointing.",
          extra: ["claimed", "told", "were"]
        },

        {
          type: "input",
          q: "Marcus's final version: 'Dr. Lena ___ that the science was solid but ___ that the communication had been imperfect.' (explain / admit — past simple)",
          a: "explained / admitted",
          alts: ["Explained / admitted"],
          successTip: "EXPLAINED (solid science = positive, neutral) + ADMITTED (imperfect communication = difficult to say). The two verbs together paint a complex, honest portrait. That's great reporting! 📰🎯"
        }
      ]
    },

    // ===================================================================
    // SET 6: CLAIMED (B2-)
    // ===================================================================
    {
      id: "m10-u10-set6",
      title: "Set 6: Unverified Assertions",
      context: `*Johannesburg, Friday — Marcus files his article*

While Marcus was writing, a rival journalist published a competing story.
In it, a different scientist made extraordinary statements about the same research.
Marcus read it carefully. Something felt wrong.
"When you can't verify something," Priya had told him, "use 'claimed'."

*Johannesburgo, viernes — Marcus entrega su artículo*

Mientras Marcus escribía, un periodista rival publicó una historia sobre el mismo tema.
En ella, un científico diferente hacía afirmaciones extraordinarias sobre la misma investigación.
Marcus la leyó atentamente. Algo no cuadraba.
"Cuando no puedes verificar algo," le había dicho Priya, "usa 'claimed'."`,

      questions: [
        {
          type: "match",
          q: "Match 'claimed' to its journalistic function:",
          pairs: [
            { left: "claimed", right: "afirmó/aseguró (sin verificación — el periodista toma distancia)" },
            { left: "claimed vs explained", right: "Claimed = duda implícita. Explained = información aceptada." },
            { left: "journalistic use", right: "'He claimed the cure existed.' = We don't know if it's true." }
          ],
          a: "completed",
          successTip: "'Claimed' = journalistic distance! The reporter is saying: 'This person said X, but I'm not confirming it's true.' Powerful tool for responsible reporting! 🔍📰"
        },

        {
          type: "mc",
          q: "The rival scientist: 'My treatment cures the condition in 90% of cases.' → He ___ that his treatment ___ the condition in 90% of cases.",
          options: ["claimed / cured", "explained / cured", "admitted / had cured"],
          a: "claimed / cured",
          tip: "Unverified assertion = claimed. Cures → cured (backshift).",
          successTip: "CLAIMED = journalist doesn't confirm it's true. CURES → CURED (backshift). If it had been verified, EXPLAINED would be used. One verb change = huge difference in credibility! 🔍"
        },

        {
          type: "mc",
          q: "The rival article ___ that the treatment ___ already in use in three countries — but provided no evidence.",
          options: ["claimed / was", "explained / had been", "admitted / was"],
          a: "claimed / was",
          tip: "No evidence = claimed. Is → was (backshift).",
          successTip: "'No evidence provided' = the classic signal for CLAIMED. The moment a journalist can't verify something, CLAIMED protects them legally and editorially! ⚖️"
        },

        {
          type: "mc",
          q: "Choose the sentence where 'claimed' is used correctly:",
          options: [
            "The scientist claimed that a cure had been found, though no data was published.",
            "The scientist claimed me that a cure had been found.",
            "The scientist claimed that a cure has been found."
          ],
          a: "The scientist claimed that a cure had been found, though no data was published.",
          tip: "Claimed = no person object. Has been → had been (backshift). 'No data published' = unverified = claimed.",
          successTip: "Error detection! 'Claimed me' ❌ (no person object). 'Has been found' ❌ (forgot backshift). Option 1: correct structure AND 'though no data' confirms the unverified claim! 🔎"
        },

        {
          type: "mc",
          q: "Marcus vs the rival journalist: Marcus ___ Dr. Lena's results carefully before reporting. The rival journalist simply ___ the scientist's words ___ true.",
          options: ["verified / claimed / were", "explained / claimed / had been", "admitted / agreed / were"],
          a: "verified / claimed / were",
          tip: "Verified = checked. Claimed = reported without checking. Were = backshift of are.",
          successTip: "VERIFIED (Marcus's careful journalism) vs CLAIMED (rival's unverified reporting). The contrast of these two verbs tells us everything about each journalist's credibility! 🏆"
        },

        {
          type: "input",
          q: "Rival article: 'The treatment will be available within six months.' → The article ___ that the treatment ___ be available within six months. (claimed + backshift)",
          a: "claimed / would",
          alts: ["Claimed / would"],
          tip: "Claimed = unverified future claim. Will → would (backshift).",
          successTip: "Will → would. CLAIMED = the journalist is not confirming this will happen. 'The article claimed it would happen' = we heard it, but we can't promise it! 📰"
        },

        {
          type: "mc",
          q: "Dr. Lena responded to the rival article. She ___ that the numbers ___ false and ___ that no independent trials ___ ever been conducted.",
          options: ["claimed / were / added / had", "explained / had been / added / had", "admitted / were / claimed / had"],
          a: "explained / had been / added / had",
          tip: "Dr. Lena stating verified facts = explained. Supplementary point = added. Were/has been → had been (backshift).",
          successTip: "EXPLAINED (her verified response) + ADDED (extra point). She is NOT claiming — she KNOWS these facts. CLAIMED would imply even SHE had doubts! Verb choice = credibility! 🔬"
        },

        {
          type: "mc",
          q: "Priya reviewed Marcus's piece: 'You wrote that the rival scientist explained the results. But you couldn't verify them. You should have written that he ___ them.'",
          options: ["claimed", "admitted", "agreed"],
          a: "claimed",
          tip: "Unverified = claimed. Explained implies acceptance of the facts.",
          successTip: "Editorial lesson! EXPLAINED = I accept this is true. CLAIMED = I'm reporting it but can't confirm it. This distinction protects journalists — and readers! 🖊️⚖️"
        },

        {
          type: "order",
          q: "Reorder: 'El artículo rival afirmaba que el tratamiento era revolucionario, pero no aportaba ninguna prueba que lo respaldara.'",
          words: ["The", "rival", "article", "claimed", "that", "the", "treatment", "was", "revolutionary,", "but", "provided", "no", "evidence", "to", "support", "it."],
          a: "The rival article claimed that the treatment was revolutionary, but provided no evidence to support it.",
          extra: ["explained", "told", "had"]
        },

        {
          type: "input",
          q: "Marcus's final paragraph: 'While the rival scientist ___ that results were conclusive, Dr. Lena ___ that further trials were essential.' (claim + explain — past simple)",
          a: "claimed / explained",
          alts: ["Claimed / explained"],
          successTip: "CLAIMED (unverified rival) vs EXPLAINED (verified scientist). Two verbs, two levels of credibility, one powerful contrast. That's responsible journalism! 📰🔬"
        }
      ]
    },

    // ===================================================================
    // SET 7: ARGUED (B2)
    // ===================================================================
    {
      id: "m10-u10-set7",
      title: "Set 7: Making a Case",
      context: `*Vienna, Saturday — International Science Conference*

Dr. Lena had been invited to present her research at a major conference.
She was not just explaining — she was making an argument.
"Argued is not about fighting," Priya had once written.
"It's about presenting a reasoned, logical position."

*Viena, sábado — Conferencia Internacional de Ciencias*

La Dra. Lena había sido invitada a presentar su investigación en una gran conferencia.
No solo explicaba — estaba construyendo un argumento.
"Argued no es sobre peleas," había escrito Priya una vez.
"Es sobre presentar una posición razonada y lógica."`,

      questions: [
        {
          type: "match",
          q: "Match 'argued' to its academic meaning:",
          pairs: [
            { left: "argued", right: "argumentó (presentó una posición lógica y razonada)" },
            { left: "argued vs claimed", right: "Argued = con lógica/evidencia. Claimed = sin verificación." },
            { left: "argued vs admitted", right: "Argued = posición intelectual. Admitted = verdad personal difícil." }
          ],
          a: "completed",
          successTip: "ARGUED = presented a case with reasoning. NOT a fight! 'She argued that X because of Y.' This is the academic/journalistic 'argued' — logical, structured, evidenced! 🎓"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'The evidence clearly supports early intervention.' → She ___ that the evidence clearly ___ early intervention.",
          options: ["argued / supported", "claimed / supports", "explained / had supported"],
          a: "argued / supported",
          tip: "Reasoned position with evidence = argued. Supports → supported (backshift).",
          successTip: "ARGUED = her position was logical and supported by evidence. If she had no evidence, CLAIMED. If she was just sharing information neutrally, EXPLAINED! Three different verbs, three different levels of certainty! 🔬"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'Delaying the trials will cost lives.' → She ___ that delaying the trials ___ cost lives.",
          options: ["argued / would", "claimed / will", "admitted / would"],
          a: "argued / would",
          tip: "Strong reasoned position = argued. Will → would (backshift).",
          successTip: "ARGUED (strong logical position) + WOULD (will backshifted). She's not guessing (claimed) or confessing (admitted) — she's making a case! 🎓"
        },

        {
          type: "mc",
          q: "The rival scientist ___ that Dr. Lena's method was outdated. Dr. Lena ___ that his data was three years old.",
          options: ["argued / argued", "claimed / argued", "argued / admitted"],
          a: "claimed / argued",
          tip: "Rival = no evidence shown = claimed. Dr. Lena = had evidence = argued.",
          successTip: "CLAIMED (rival, no evidence) vs ARGUED (Dr. Lena, with evidence). One word tells you who the journalist believes! Verb choice = editorial stance! 📰"
        },

        {
          type: "mc",
          q: "Choose the sentence where 'argued' is used correctly:",
          options: [
            "She argued that the methodology was sound because three independent labs had verified it.",
            "She argued me that the methodology was sound.",
            "She argued that the methodology is sound."
          ],
          a: "She argued that the methodology was sound because three independent labs had verified it.",
          tip: "Argued = no person object. Is → was (backshift). Evidence provided = argued, not claimed.",
          successTip: "Error detection + context! Option 2: 'argued me' ❌ (no person object). Option 3: forgot backshift (is → was). Option 1: correct structure AND has evidence! 🔎"
        },

        {
          type: "input",
          q: "Conference summary: 'Dr. Lena argued that current protocols ___ ___ updated to reflect new evidence.' (need → Past Perfect in reported speech)",
          a: "had needed",
          alts: ["Had needed"],
          tip: "Need (Present Simple) → needed (backshift). But if had needed was already in the original, stays. Context: argued = reasoned case.",
          successTip: "ARGUED that protocols needed updating = her position based on evidence. Not a guess (claimed), not a confession (admitted). A logical case! 🎓✅"
        },

        {
          type: "mc",
          q: "Yuki ___ that the sample size ___ large enough, but Dr. Lena ___ that for a rare condition, it ___ the standard approach.",
          options: ["argued / wasn't / argued / was", "claimed / wasn't / explained / had been", "admitted / had been / argued / was"],
          a: "argued / wasn't / argued / was",
          tip: "Both presenting reasoned positions = both argued. Wasn't/was = past (no further backshift needed).",
          successTip: "Two people can both ARGUE in the same reported exchange! ARGUED ≠ fought. They each presented logical positions. Was/wasn't = already past, stays as is! 🤝🎓"
        },

        {
          type: "mc",
          q: "Priya wrote: 'Dr. Lena ___ persuasively that the treatment ___ be fast-tracked for approval, presenting data that the committee ___ not previously seen.'",
          options: ["argued / should / had", "claimed / would / had", "explained / would / hadn't"],
          a: "argued / should / had",
          tip: "Persuasive logical case = argued. Should (no backshift). Previously seen → had not seen (Past Perfect).",
          successTip: "ARGUED (persuasive case) + SHOULD (no backshift for modals!) + HAD NOT SEEN (Past Perfect for earlier event). Three grammar points, one expert sentence! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Ella argumentó que los datos respaldaban su posición y que ignorarlos sería un error científico.'",
          words: ["She", "argued", "that", "the", "data", "supported", "her", "position", "and", "that", "ignoring", "it", "would", "be", "a", "scientific", "error."],
          a: "She argued that the data supported her position and that ignoring it would be a scientific error.",
          extra: ["claimed", "told", "had"]
        },

        {
          type: "input",
          q: "Priya's summary: 'While the rival ___ that results were definitive, Dr. Lena ___ that independent verification was still required.' (claim / argue — past simple)",
          a: "claimed / argued",
          alts: ["Claimed / argued"],
          successTip: "CLAIMED (no evidence, unverified) vs ARGUED (logical, evidence-based). The contrast in one sentence. Priya's choice of verbs tells readers exactly who to trust! 🔬📰"
        }
      ]
    },

    // ===================================================================
    // SET 8: SUGGESTED (B2)
    // ===================================================================
    {
      id: "m10-u10-set8",
      title: "Set 8: Soft Proposals",
      context: `*Vienna, Saturday evening — research team dinner*

After the conference, Dr. Lena, Yuki, and Marcus had dinner together.
The pressure had lifted. Now they were talking about what to do next.
Several ideas came up. Nobody was arguing — just thinking out loud.
"In a room like that," Marcus said later, "everyone suggests something."

*Viena, sábado por la noche — cena del equipo de investigación*

Tras la conferencia, la Dra. Lena, Yuki y Marcus cenaron juntos.
La presión había desaparecido. Ahora hablaban de qué hacer después.
Surgieron varias ideas. Nadie argumentaba — solo pensaba en voz alta.
"En una sala como esa," dijo Marcus después, "todo el mundo sugiere algo."`,

      questions: [
        {
          type: "match",
          q: "Match 'suggested' to its three structures:",
          pairs: [
            { left: "suggested + that + should", right: "'She suggested that they should publish soon.'" },
            { left: "suggested + that + base verb (subjunctive)", right: "'She suggested that they publish soon.' (formal)" },
            { left: "suggested + gerund", right: "'She suggested publishing soon.' (no that, no person)" }
          ],
          a: "completed",
          successTip: "SUGGESTED has three structures! + that should (common), + that + base verb (formal subjunctive), + gerund (no person). All three are correct — context decides which! 💡"
        },

        {
          type: "mc",
          q: "Yuki: 'Why don't you publish the results in two separate papers?' → She ___ that Dr. Lena ___ the results in two separate papers.",
          options: ["suggested / publish", "argued / published", "suggested / publishes"],
          a: "suggested / publish",
          tip: "Suggestion = suggested. Subjunctive = base verb (publish, not published or publishes).",
          successTip: "SUGGESTED that + base verb (subjunctive)! No backshift for the subjunctive form! 'She suggested that they publish' ✅ not 'published' ❌ — the base verb never changes! 🔮"
        },

        {
          type: "mc",
          q: "Marcus: 'You should hold a press conference next week.' → He ___ that Dr. Lena ___ hold a press conference the following week.",
          options: ["suggested / should", "argued / should", "claimed / would"],
          a: "suggested / should",
          tip: "Soft recommendation = suggested. Should = no backshift (modal). Next week → the following week (time shift).",
          successTip: "SHOULD doesn't backshift! 'Should hold' stays as 'should hold'. AND next week → the following week (time adverb shifts even when modal doesn't)! ⏰"
        },

        {
          type: "mc",
          q: "Dr. Lena: 'We could collaborate with the Vienna institute.' → She ___ ___ with the Vienna institute.",
          options: ["suggested collaborating", "suggested to collaborate", "suggested collaborate"],
          a: "suggested collaborating",
          tip: "Suggested + gerund (no that, no person). Never suggested + to + infinitive!",
          successTip: "SUGGESTED + gerund = 'suggested doing'. NEVER 'suggested to do' ❌ — this is a very common error! 'She suggested going', 'suggested leaving', 'suggested collaborating'. Always -ing! 🔄"
        },

        {
          type: "mc",
          q: "Choose the sentence where 'suggested' is used correctly:",
          options: [
            "Yuki suggested that the team take a break before the next phase.",
            "Yuki suggested the team to take a break before the next phase.",
            "Yuki suggested that the team will take a break before the next phase."
          ],
          a: "Yuki suggested that the team take a break before the next phase.",
          tip: "Suggested + that + base verb (subjunctive). No 'to'. No backshift of will.",
          successTip: "Error detection! 'Suggested to take' ❌ (no infinitive after suggested). 'Will take' ❌ (will should not appear here — either should or base verb). 'Take' ✅ = subjunctive! 🔎"
        },

        {
          type: "input",
          q: "Marcus: 'You should consider a book deal.' → He suggested that Dr. Lena ___ a book deal. (consider — subjunctive form)",
          a: "consider",
          alts: ["Consider"],
          tip: "Suggested + that + base verb. Consider = base form (no change). No 'should' needed here.",
          successTip: "BASE VERB in subjunctive = the verb in its dictionary form. 'Consider' stays as 'consider'. No -s, no -ed, no change. Pure and simple! 📚"
        },

        {
          type: "mc",
          q: "Yuki ___ that the team ___ a celebration dinner, ___ that they had all worked incredibly hard, and ___ that the next phase ___ not begin for at least a month.",
          options: ["suggested / have / added / argued / should", "suggested / have / added / suggested / should", "argued / have / suggested / added / would"],
          a: "suggested / have / added / suggested / should",
          tip: "Two suggestions + one additional fact. Suggested (dinner) + added (worked hard) + suggested (delay) + have (subjunctive) + should (modal, no backshift).",
          successTip: "Four verbs in one sentence! SUGGESTED (dinner proposal) + ADDED (extra fact) + SUGGESTED (second proposal) + SHOULD (modal, no backshift). Real complex reported speech! 🏆"
        },

        {
          type: "mc",
          q: "Dr. Lena ___ that the book was a good idea but ___ working on the second paper first, arguing that the data ___ still being analysed.",
          options: ["agreed / suggested / was", "admitted / suggested / had been", "agreed / claimed / was"],
          a: "agreed / suggested / was",
          tip: "Agreement + soft recommendation + ongoing process. Agreed (shared view), suggested + gerund (soft proposal), was (already past).",
          successTip: "AGREED (shared view) + SUGGESTED + gerund (soft proposal, no that-clause) + WAS (past, no further backshift). Three verbs, three functions, all correct! 🎯"
        },

        {
          type: "order",
          q: "Reorder: 'Marcus sugirió que celebraran el éxito, y Yuki añadió que todos merecían un descanso.'",
          words: ["Marcus", "suggested", "that", "they", "celebrate", "the", "success,", "and", "Yuki", "added", "that", "everyone", "deserved", "a", "break."],
          a: "Marcus suggested that they celebrate the success, and Yuki added that everyone deserved a break.",
          extra: ["argued", "celebrates", "deserves"]
        },

        {
          type: "input",
          q: "Dinner summary: 'By the end of the evening, everyone had ___ something — and nobody had ___ or ___.' (suggest / claim / argue — past participle)",
          a: "suggested / claimed / argued",
          alts: ["Suggested / claimed / argued"],
          successTip: "Suggested (soft proposals) vs claimed (unverified) vs argued (logical positions) — in one reflective sentence. This was a collaborative dinner, not a debate! 🍽️✅"
        }
      ]
    },

    // ===================================================================
    // SET 9: CLAIMED vs ARGUED — ACADEMIC CONTRAST (B2+)
    // ===================================================================
    {
      id: "m10-u10-set9",
      title: "Set 9: Evidence vs Assertion",
      context: `*Mumbai, Sunday — Priya reviews both articles*

Priya had received both Marcus's article and the rival journalist's piece.
As an experienced writer, she could see the difference immediately.
"One of these journalists argued their case," she said to Yemi on the phone.
"The other one just claimed things and hoped nobody would check."

*Bombay, domingo — Priya revisa ambos artículos*

Priya había recibido tanto el artículo de Marcus como el del periodista rival.
Como escritora experimentada, veía la diferencia de inmediato.
"Uno de estos periodistas argumentó su caso," le dijo a Yemi por teléfono.
"El otro simplemente afirmó cosas y esperó que nadie lo comprobara."`,

      questions: [
        {
          type: "match",
          q: "Match the academic contrast between claimed and argued:",
          pairs: [
            { left: "claimed", right: "Asserts without evidence — reader should be sceptical." },
            { left: "argued", right: "Presents a case with reasoning — reader can evaluate the logic." },
            { left: "key difference", right: "CLAIMED = take with a pinch of salt. ARGUED = engage with the logic." }
          ],
          a: "completed",
          successTip: "The academic distinction: CLAIMED = assertion (believe me). ARGUED = reasoning (here's why). In essays and journalism, ARGUED = credibility. CLAIMED = possible unreliability! 🎓"
        },

        {
          type: "mc",
          q: "The rival journalist ___ that the treatment ___ a 99% success rate — but cited no studies.",
          options: ["claimed / had", "argued / had", "explained / had"],
          a: "claimed / had",
          tip: "No studies cited = claimed. Has → had (backshift).",
          successTip: "No evidence = CLAIMED. Remember from Unit 7: 'take it with a pinch of salt'! A claim without evidence = pinch of salt territory! 🧂📰"
        },

        {
          type: "mc",
          q: "Marcus ___ that Dr. Lena's methodology ___ superior, citing three peer-reviewed studies that ___ the same conclusion.",
          options: ["argued / was / had reached", "claimed / was / reached", "explained / had been / reached"],
          a: "argued / was / had reached",
          tip: "Evidence cited = argued. Was = backshift. Had reached = Past Perfect (studies' finding before the article).",
          successTip: "ARGUED (with evidence) + WAS (backshift) + HAD REACHED (earlier past Perfect). Three layers! The peer-reviewed studies happened before Marcus wrote, so Past Perfect! ⏪"
        },

        {
          type: "mc",
          q: "Priya told Yemi: 'The rival journalist simply ___ that results ___ definitive. Marcus ___ they ___ reliable, and showed exactly why.'",
          options: ["claimed / were / argued / were", "argued / had been / claimed / were", "explained / were / argued / had been"],
          a: "claimed / were / argued / were",
          tip: "Rival = no evidence = claimed. Marcus = with reasoning = argued. Both backshift are → were.",
          successTip: "CLAIMED (rival, no evidence) vs ARGUED (Marcus, with reasoning). Same information presented two ways. This contrast is why verb choice matters so much in journalism! 📰"
        },

        {
          type: "mc",
          q: "Choose the sentence that describes credible academic reporting:",
          options: [
            "The researcher argued that the data supported early intervention, based on a five-year longitudinal study.",
            "The researcher claimed that the data supported early intervention, based on a five-year longitudinal study.",
            "The researcher admitted that the data supported early intervention."
          ],
          a: "The researcher argued that the data supported early intervention, based on a five-year longitudinal study.",
          tip: "Evidence provided (five-year study) = argued. Claimed would undermine the evidence. Admitted implies reluctance.",
          successTip: "Evidence present = ARGUED, not claimed! Even with evidence, some writers mistakenly use CLAIMED and accidentally undermine the research. Use ARGUED when there's evidence! 🎓"
        },

        {
          type: "mc",
          q: "The rival journalist ___ that Dr. Lena ___ data, while Dr. Lena ___ that every figure ___ independently verified.",
          options: ["claimed / had hidden / argued / had been", "argued / hid / claimed / was", "explained / had hidden / admitted / had been"],
          a: "claimed / had hidden / argued / had been",
          tip: "Unverified accusation = claimed. Evidence-based defence = argued. Hidden → had hidden (backshift). Was → had been (backshift).",
          successTip: "CLAIMED (unverified accusation) vs ARGUED (evidenced defence). Had hidden (Past Perfect backshift) + had been (passive Past Perfect). Maximum complexity — maximum marks! 🏆"
        },

        {
          type: "input",
          q: "Priya's editorial: 'A journalist who ___ without evidence damages their credibility. A journalist who ___ from evidence earns it.' (claim / argue — present simple, general truth)",
          a: "claims / argues",
          alts: ["Claims / argues"],
          successTip: "General truth = Present Simple (no backshift needed for universal statements)! CLAIMS (no evidence = damage) vs ARGUES (evidence = credibility). Priya's golden rule! 🌟🖊️"
        },

        {
          type: "mc",
          q: "Marcus's published article ___ that Dr. Lena's research ___ a significant step forward, ___ that independent replication ___ already underway, and ___ that publication ___ not be delayed further.",
          options: ["argued / was / added / was / suggested / should", "claimed / had been / added / was / argued / would", "explained / was / argued / had been / suggested / should"],
          a: "argued / was / added / was / suggested / should",
          tip: "Logical case = argued. Extra info = added. Soft proposal = suggested. Should = no backshift. All present → past (was).",
          successTip: "ARGUED (main case) + ADDED (extra detail) + SUGGESTED (soft proposal) + SHOULD (no backshift). Five decisions in one sentence — that's the full unit in one line! 🏆"
        },

        {
          type: "order",
          q: "Reorder: 'El artículo rival afirmó sin pruebas que la investigación era defectuosa, mientras que Marcus argumentó con datos que era sólida.'",
          words: ["The", "rival", "article", "claimed", "without", "evidence", "that", "the", "research", "was", "flawed,", "while", "Marcus", "argued", "with", "data", "that", "it", "was", "sound."],
          a: "The rival article claimed without evidence that the research was flawed, while Marcus argued with data that it was sound.",
          extra: ["explained", "told", "had"]
        },

        {
          type: "input",
          q: "Priya's final verdict: 'Marcus ___ his case carefully and ___ when he wasn't sure. The rival simply ___ and hoped for the best.' (argue / admit / claim — past simple)",
          a: "argued / admitted / claimed",
          alts: ["Argued / admitted / claimed"],
          successTip: "ARGUED (careful case) + ADMITTED (honest uncertainty) + CLAIMED (unverified assertions). Three verbs, three characters, three levels of journalistic integrity. Perfect unit contrast! 🏆📰"
        }
      ]
    },

    // ===================================================================
    // SET 10: ALL VERBS — FULL NARRATIVE (B2+)
    // ===================================================================
    {
      id: "m10-u10-set10",
      title: "Set 10: The Complete Story",
      context: `*London, Monday — the articles are published worldwide*

Both articles went live at the same time.
Within hours, the scientific community had responded.
Dr. Lena was answering calls. Marcus was defending his work.
Priya was watching it all — and writing the definitive account.

*Londres, lunes — los artículos se publican en todo el mundo*

Ambos artículos se publicaron al mismo tiempo.
En pocas horas, la comunidad científica había respondido.
La Dra. Lena respondía llamadas. Marcus defendía su trabajo.
Priya lo observaba todo — y escribía el relato definitivo.`,

      questions: [
        {
          type: "mc",
          q: "Three scientists read Marcus's article. Which response matches each verb? 'I think this is correct.' / 'I think this is wrong, and here's why.' / 'I think this might be right.'",
          options: [
            "Agreed / Argued against / Suggested it might be",
            "Admitted / Claimed / Argued",
            "Explained / Admitted / Added"
          ],
          a: "Agreed / Argued against / Suggested it might be",
          tip: "Shared position = agreed. Logical counter-case = argued. Tentative proposal = suggested.",
          successTip: "The whole unit in three verbs! AGREED (shared belief), ARGUED (counter-reasoning), SUGGESTED (soft possibility). Real scientific discourse uses all three constantly! 🔬"
        },

        {
          type: "mc",
          q: "The leading scientist ___ that Marcus's methodology ___ rigorous, ___ that the sample size ___ small, but ___ that the core argument ___ sound.",
          options: ["agreed / was / admitted / was / argued / was", "claimed / had been / added / was / suggested / had been", "explained / was / agreed / had / argued / was"],
          a: "agreed / was / admitted / was / argued / was",
          tip: "Agreement + honest admission + logical conclusion. All present → past (was).",
          successTip: "AGREED (endorsement) + ADMITTED (honest limitation) + ARGUED (logical conclusion). Three verbs in perfect sequence — endorsement, honesty, reasoning. Elite reported speech! 🏆"
        },

        {
          type: "mc",
          q: "Dr. Lena ___ on the radio that the rivalry ___ the result of competitive funding, ___ that this was damaging science, and ___ that collaboration ___ always produce better results.",
          options: ["explained / was / argued / suggested / would", "admitted / had been / claimed / suggested / will", "claimed / was / added / argued / had"],
          a: "explained / was / argued / suggested / would",
          tip: "Neutral context = explained. Logical position = argued. Soft proposal = suggested. Will → would (backshift).",
          successTip: "EXPLAINED (neutral context) + ARGUED (logical position) + SUGGESTED (collaborative proposal) + WOULD (will backshifted). Four verbs, four functions, all in one interview! 🎙️"
        },

        {
          type: "mc",
          q: "The rival journalist ___ he had acted professionally, but Priya ___ that two of his sources ___ fabricated.",
          options: ["claimed / argued / had been", "explained / claimed / had been", "admitted / argued / were"],
          a: "claimed / argued / had been",
          tip: "Unverified self-defence = claimed. Evidence-based accusation = argued. Were → had been (backshift).",
          successTip: "CLAIMED (rival defending himself, unverified) vs ARGUED (Priya, with evidence). Had been = Past Perfect (fabricated before article published). The contrast is devastating! 📰⚖️"
        },

        {
          type: "mc",
          q: "Marcus ___ to Priya that he had double-checked everything, ___ that one source had been harder to verify, but ___ it had been worth the effort.",
          options: ["explained / admitted / added", "argued / claimed / suggested", "agreed / explained / added"],
          a: "explained / admitted / added",
          tip: "Professional confirmation = explained. Honest challenge = admitted. Final supplementary note = added.",
          successTip: "EXPLAINED (professional) + ADMITTED (honest) + ADDED (supplementary). This three-verb sequence = credibility! Anyone who explains, admits, and adds is trustworthy! 🏆✅"
        },

        {
          type: "input",
          q: "Yuki's statement: 'The research speaks for itself.' → Yuki ___ that the research ___ for itself. (argue — past simple + backshift)",
          a: "argued / spoke",
          alts: ["Argued / spoke"],
          tip: "Speaks → spoke (backshift). Argued = logical position, not a claim.",
          successTip: "ARGUED (not claimed!) because Yuki had evidence. Speaks → spoke (present backshifts to past). Even short statements need both verb choice AND backshift! 🔬"
        },

        {
          type: "mc",
          q: "Dr. Lena's final press statement used all seven verbs. Match each to its function:",
          options: [
            "Explained (facts) → Admitted (errors) → Added (extra) → Agreed (shared) → Argued (logic) → Suggested (future) → Claimed is absent — she had evidence for everything.",
            "Claimed (all facts) → Admitted (errors) → Added (extra) → Agreed (shared) → Argued (logic) → Suggested (future) → Explained is absent.",
            "Explained (facts) → Argued (errors) → Suggested (extra) → Agreed (shared) → Claimed (logic) → Added (future) → Admitted is absent."
          ],
          a: "Explained (facts) → Admitted (errors) → Added (extra) → Agreed (shared) → Argued (logic) → Suggested (future) → Claimed is absent — she had evidence for everything.",
          tip: "Claimed = unverified. A scientist with evidence would never need to claim — they explain, argue, or suggest.",
          successTip: "Dr. Lena used six verbs and AVOIDED claimed — because she had evidence for everything! That's the mark of genuine expertise. CLAIMED = the verb you use when you have nothing else! 🔬🏆"
        },

        {
          type: "order",
          q: "Reorder: 'Ella explicó los hechos, admitió los errores, argumentó su caso con datos y sugirió un camino a seguir.'",
          words: ["She", "explained", "the", "facts,", "admitted", "the", "errors,", "argued", "her", "case", "with", "data,", "and", "suggested", "a", "way", "forward."],
          a: "She explained the facts, admitted the errors, argued her case with data, and suggested a way forward.",
          extra: ["claimed", "told", "adding"]
        },

        {
          type: "mc",
          q: "Priya's closing paragraph used four verbs in sequence. Choose the correct order for this context: 'Dr. Lena [gave neutral information]. She [confessed a difficulty]. Marcus [made a logical case]. Both [had a shared position].'",
          options: [
            "explained / admitted / argued / agreed",
            "argued / claimed / explained / agreed",
            "admitted / explained / suggested / argued"
          ],
          a: "explained / admitted / argued / agreed",
          tip: "Neutral = explained. Confession = admitted. Logic = argued. Shared position = agreed.",
          successTip: "The perfect four-verb sequence: EXPLAINED (information) → ADMITTED (honesty) → ARGUED (logic) → AGREED (resolution). This is the arc of every great scientific dispute! 🔬📰"
        },

        {
          type: "input",
          q: "Final sentence: 'In the end, Priya ___ that good journalism and good science shared one quality: the courage to ___ when you are wrong, and the evidence to ___ when you are right.' (argue — past simple / admit — infinitive / argue — infinitive)",
          a: "argued / admit / argue",
          alts: ["Argued / admit / argue"],
          successTip: "🏆 UNIT 10 COMPLETE! All 7 reporting verbs mastered ✅ Backshift across all tenses ✅ Verb choice for tone and credibility ✅ Complex multi-verb sentences ✅ You report like a professional writer! 📰🔬🌍"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u10-reading",
    title: "Reading: The Science Story",
    category: "reading",

    questions: [

      // --- PASSAGE 1 ---
      {
        type: "passage",
        passage: `**The Press Conference**

Dr. Lena explained that the treatment had taken six years to develop. She admitted that the first trial had failed but added that subsequent results had been promising. Marcus noted that she had answered every question without hesitation.`
      },
      {
        type: "mc",
        q: "How long did Dr. Lena say the treatment had taken?",
        options: ["Six years", "Three years", "Ten years"],
        a: "Six years",
        tip: "'...the treatment had taken six years to develop.'"
      },
      {
        type: "mc",
        q: "What did she admit about the first trial?",
        options: ["That it had failed", "That it had been cancelled", "That results had been unclear"],
        a: "That it had failed",
        tip: "'She admitted that the first trial had failed.'"
      },

      // --- PASSAGE 2 ---
      {
        type: "passage",
        passage: `**The Rival Article**

The rival journalist claimed that Dr. Lena's treatment was already obsolete. He argued that newer approaches had superseded it, though he cited no supporting data. Priya read it and immediately took the claims with a pinch of salt.`
      },
      {
        type: "mc",
        q: "What did the rival journalist claim about the treatment?",
        options: ["That it was already obsolete", "That it had never been tested", "That it would be delayed"],
        a: "That it was already obsolete",
        tip: "'...claimed that Dr. Lena's treatment was already obsolete.'"
      },
      {
        type: "mc",
        q: "Why did Priya take the claims with a pinch of salt?",
        options: ["No supporting data was cited", "She trusted Dr. Lena more", "The article was too long"],
        a: "No supporting data was cited",
        tip: "'...he cited no supporting data.' = reason for scepticism."
      },

      // --- PASSAGE 3 ---
      {
        type: "passage",
        passage: `**Yuki's Statement**

Yuki agreed that the research had limitations but argued that the core findings were valid. She added that three independent laboratories had replicated the results. She suggested that a larger trial should begin the following year.`
      },
      {
        type: "mc",
        q: "What did Yuki agree about the research?",
        options: ["That it had limitations", "That it should be cancelled", "That the methodology was flawed"],
        a: "That it had limitations",
        tip: "'Yuki agreed that the research had limitations.'"
      },
      {
        type: "mc",
        q: "What did Yuki suggest?",
        options: ["That a larger trial should begin the following year", "That the results should be published immediately", "That the rival article should be removed"],
        a: "That a larger trial should begin the following year",
        tip: "'She suggested that a larger trial should begin the following year.'"
      },

      // --- PASSAGE 4 ---
      {
        type: "passage",
        passage: `**Marcus's Defence**

Marcus explained that he had verified every source before publishing. He admitted that one figure had been difficult to confirm but argued that the overall conclusion was supported by the data. He added that a correction would be issued if needed.`
      },
      {
        type: "mc",
        q: "What did Marcus admit?",
        options: ["That one figure had been difficult to confirm", "That he had not verified his sources", "That the article contained errors"],
        a: "That one figure had been difficult to confirm",
        tip: "'He admitted that one figure had been difficult to confirm.'"
      },
      {
        type: "mc",
        q: "What did Marcus say he would do if needed?",
        options: ["Issue a correction", "Republish the article", "Interview Dr. Lena again"],
        a: "Issue a correction",
        tip: "'He added that a correction would be issued if needed.'"
      },

      // --- PASSAGE 5 ---
      {
        type: "passage",
        passage: `**Priya's Verdict**

Priya argued that the case showed the difference between journalism and assertion. She explained that Marcus had argued from evidence while the rival had merely claimed. She suggested that editors should always ask: argued or claimed? The answer, she added, told you everything.`
      },
      {
        type: "mc",
        q: "What distinction did Priya argue the case had shown?",
        options: ["Between journalism and assertion", "Between science and opinion", "Between facts and feelings"],
        a: "Between journalism and assertion",
        tip: "'...the difference between journalism and assertion.'"
      },
      {
        type: "mc",
        q: "What question did Priya suggest editors should always ask?",
        options: ["Argued or claimed?", "Explained or admitted?", "Agreed or suggested?"],
        a: "Argued or claimed?",
        tip: "'She suggested that editors should always ask: argued or claimed?'",
        successTip: "All seven reporting verbs appeared in these five passages — each doing a specific job. That's Unit 10 and Module 10 complete! You've mastered reported speech at its most sophisticated level! 🏆🌍"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u10-review",
    title: "Unit 10 Review: Reporting Verbs + That",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: She ___ that the experiment had taken six years to complete.",
        options: ["explained", "told", "spoke"],
        a: "explained",
        tip: "Neutral factual information = explained + that clause."
      },
      {
        type: "mc",
        q: "Review: He ___ that the first results had been disappointing.",
        options: ["admitted", "argued", "suggested"],
        a: "admitted",
        tip: "Reluctant confession = admitted. Backshift: were → had been."
      },
      {
        type: "mc",
        q: "Review: She explained the timeline and ___ that funding had also been cut.",
        options: ["added", "claimed", "argued"],
        a: "added",
        tip: "Supplementary information after a prior statement = added."
      },
      {
        type: "mc",
        q: "Review: They ___ that more trials were necessary before any publication.",
        options: ["agreed", "explained", "suggested"],
        a: "agreed",
        tip: "Shared position between two or more people = agreed + that clause."
      },
      {
        type: "mc",
        q: "Review: The rival journalist ___ that the cure already existed — but provided no evidence.",
        options: ["claimed", "argued", "explained"],
        a: "claimed",
        tip: "Unverified assertion with no evidence = claimed."
      },
      {
        type: "mc",
        q: "Review: Dr. Lena ___ that the data clearly supported early intervention, citing three peer-reviewed studies.",
        options: ["argued", "claimed", "admitted"],
        a: "argued",
        tip: "Logical position backed by evidence = argued."
      },
      {
        type: "mc",
        q: "Review: Yuki ___ that the team ___ a shorter publication timeline.",
        options: ["suggested / consider", "suggested / considers", "argued / consider"],
        a: "suggested / consider",
        tip: "Soft recommendation = suggested. Subjunctive = base verb (consider, not considers)."
      },
      {
        type: "mc",
        q: "Review: She explained the results, ___ the setbacks, ___ that funding had been cut, and ___ that collaboration was the only way forward.",
        options: ["admitted / added / suggested", "argued / claimed / added", "admitted / argued / claimed"],
        a: "admitted / added / suggested",
        tip: "Reluctant truth = admitted. Extra detail = added. Soft proposal = suggested."
      },
      {
        type: "mc",
        q: "Review: Which verb is missing? 'The rival ___ the treatment was revolutionary. Marcus ___ this was exaggerated, citing the lack of peer review.'",
        options: ["claimed / argued", "argued / claimed", "explained / admitted"],
        a: "claimed / argued",
        tip: "No evidence = claimed. Logical counter-case with evidence = argued."
      },
      {
        type: "mc",
        q: "Review: Priya ___ that good journalists always ___ when they are wrong and ___ when they are right.",
        options: ["argued / admit / argue", "explained / claimed / argued", "suggested / explain / admit"],
        a: "argued / admit / argue",
        tip: "Priya's logical position = argued. Admit (infinitive, no backshift). Argue (infinitive, no backshift).",
        successTip: "🎉 MODULE 10 COMPLETE! Reported speech ✅ Time adverbs ✅ Say vs Tell ✅ Seven reporting verbs ✅ Backshift across all tenses ✅ You report language like a professional journalist and scientist! 📰🔬🏆"
      }
    ]
  }
};
